import { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  usePropertyQuery,
  useUpdatePropertyMutation,
} from '@/hooks/queries/useProperties';
import { editPropertySchema, type EditPropertyData } from '@/schema/properties';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Save } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { BasicInformation } from './components/basic-information';
import { ContractInformation } from './components/contract-information';
import { ContactInformation } from './components/contact-information';
import { PropertyStatus } from './components/property-status';
import { ContractScope } from '@/components/properties/edit/contract-scope';

export default function EditPropertyPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  // Fetch property data
  const { data: propertyData, isLoading, error } = usePropertyQuery(id || '');

  // Update mutation
  const { mutate: updateProperty, isPending: isUpdating } =
    useUpdatePropertyMutation(id || '', {
      onSuccessCallback: () => {
        navigate('/properties');
      },
    });

  // Initialize React Hook Form
  const form = useForm<EditPropertyData>({
    resolver: zodResolver(editPropertySchema),
    defaultValues: {
      name: '',
      property_type_id: '',
      stars: 1,
      location: '',
      contact_person: '',
      contact_email: '',
      contact_phone: '',
      contract_start: '',
      contract_end: '',
      is_active: true,
      photoBase64: null,
      contractScope: 'full',
      selectedDepartmentTouchpoints: [],
      property_scope: [],
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = form;

  // Reset form when property data is loaded
  useEffect(() => {
    if (propertyData) {
      // Convert property_scope to selectedDepartmentTouchpoints format
      const scopeMap = new Map<number, number[]>();
      propertyData.property_scope?.forEach(scope => {
        const deptId = scope.department_id;
        if (!scopeMap.has(deptId)) {
          scopeMap.set(deptId, []);
        }
        scopeMap.get(deptId)!.push(scope.touchpoint_id);
      });

      const selectedDepartmentTouchpoints = Array.from(scopeMap.entries()).map(
        ([deptId, touchpointIds]) => ({
          departmentId: deptId.toString(),
          touchpointIds: touchpointIds.map(id => id.toString()),
        })
      );

      // Determine contract scope
      const contractScope =
        propertyData.property_scope && propertyData.property_scope.length > 0
          ? 'limited'
          : 'full';

      reset({
        name: propertyData.name || '',
        property_type_id: propertyData.property_type_id?.toString() || '',
        stars: propertyData.stars || 1,
        location: propertyData.location || '',
        contact_person: propertyData.contact_person || '',
        contact_email: propertyData.contact_email || '',
        contact_phone: propertyData.contact_phone || '',
        contract_start: propertyData.contract_start || '',
        contract_end: propertyData.contract_end || '',
        is_active: propertyData.is_active ?? true,
        photoBase64: null,
        contractScope: contractScope as 'full' | 'limited',
        selectedDepartmentTouchpoints: selectedDepartmentTouchpoints,
        property_scope:
          propertyData.property_scope?.map(scope => ({
            property_type_id: scope.property_type_id,
            department_id: scope.department_id,
            touchpoint_id: scope.touchpoint_id,
          })) || [],
      });
    }
  }, [propertyData, reset]);

  useEffect(() => {
    if (error) {
      toast({
        title: t('properties.edit.toasts.errorTitle'),
        description: t('properties.edit.toasts.fetchError'),
        variant: 'destructive',
      });
      navigate('/properties');
    }
  }, [error, navigate, toast, t]);

  const onSubmit = async (data: EditPropertyData) => {
    try {
      // Convert selectedDepartmentTouchpoints to property_scope format
      let property_scope: {
        property_type_id: number;
        department_id: number;
        touchpoint_id: number;
      }[] = [];

      if (
        data.contractScope === 'limited' &&
        data.selectedDepartmentTouchpoints
      ) {
        const propertyTypeId = Number(data.property_type_id);
        property_scope = data.selectedDepartmentTouchpoints.flatMap(dept =>
          dept.touchpointIds.map(touchpointId => ({
            property_type_id: propertyTypeId,
            department_id: Number(dept.departmentId),
            touchpoint_id: Number(touchpointId),
          }))
        );
      }

      updateProperty({
        name: data.name,
        property_type_id: Number(data.property_type_id),
        stars: data.stars,
        location: data.location,
        contact_person: data.contact_person,
        contact_email: data.contact_email,
        contact_phone: data.contact_phone,
        contract_start: data.contract_start,
        contract_end: data.contract_end,
        is_active: data.is_active,
        photo_base64: data.photoBase64 || undefined,
        property_scope: property_scope,
      });
    } catch (_error) {
      toast({
        title: t('properties.edit.toasts.errorTitle'),
        description: t('properties.edit.toasts.errorDesc'),
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  if (!propertyData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="bg-primary text-primary-foreground p-4 sm:p-6">
        <div className="max-w-6xl mx-auto">
          <div
            className={cn(
              'flex items-center gap-4',
              isRTL ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/properties')}
              className="text-primary-foreground hover:bg-primary-foreground/10"
            >
              <ArrowLeft className={cn('w-4 h-4', isRTL ? 'ml-2' : 'mr-2')} />
              {t('back')}
            </Button>
            <h1
              className={cn(
                'text-xl sm:text-2xl font-semibold',
                isRTL ? 'text-right' : 'text-left'
              )}
            >
              {t('properties.edit.title')}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Form Content */}
            <div className="space-y-6">
              <BasicInformation form={form} />
              <ContractInformation form={form} />
              <ContactInformation form={form} />
              <ContractScope />
              <PropertyStatus form={form} />
            </div>

            {/* Navigation Buttons */}
            <div
              className={cn(
                'flex gap-4 pt-8 border-t mt-8',
                isRTL ? 'flex-row-reverse' : 'flex-row'
              )}
            >
              <Button
                type="submit"
                disabled={isSubmitting || isUpdating}
                className="w-full sm:w-auto flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isSubmitting || isUpdating
                  ? t('properties.edit.actions.saving')
                  : t('properties.edit.actions.saveChanges')}
              </Button>

              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate('/properties')}
                className="w-full sm:w-auto"
              >
                {t('cancel')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
}
